/**
 * Money formatting for the admin console.
 *
 * The revenue-share ledger stores integer MICRO-USD (1e-6 USD) — see
 * convex/functions/lib/revshare.ts for why. The rest of the console formats
 * plain dollar numbers inline as `$${n.toLocaleString()}`, so without a
 * shared helper the two would quietly disagree about what a number means.
 */

/** Micro-USD → dollars, for display only. Never round-trip through this. */
export function usdFromMicros(micros: number): number {
  return micros / 1_000_000;
}

/**
 * Micro-USD → "$1,234.56". Always two decimals: a payout figure that renders
 * as "$1,234.5" reads like a typo on a page about paying people.
 */
export function fmtUsdMicros(micros: number): string {
  const usd = usdFromMicros(micros);
  const sign = usd < 0 ? '-' : '';
  return `${sign}$${Math.abs(usd).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/** "2026-06" → "June 2026", for statement headings. */
export function fmtPeriod(periodKey: string): string {
  const [y, m] = periodKey.split('-').map(Number);
  if (!y || !m) return periodKey;
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString(undefined, {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}
